/**
 * Centralized Content Configuration
 * Eliminates duplicate content and ensures consistency across the website
 */

// Core Company Information
export const COMPANY_INFO = {
  name: 'Bootminds Elite',
  legalName: 'BootMinds Systems Pvt Ltd.',
  numberOfEmployees: '50-100',
  tagline: 'Build GCCs That Out-Innovate, Not Out-Spend',
  description: 'Transform your Global Capability Center into an innovation powerhouse—operational in weeks, not years, with zero talent gaps or cost overruns.',
  foundingDate: '2017',
  foundingLocation: 'Bangalore, India',
  relaunchDate: '2025-01',
  website: 'https://bootminds.com',
  
  // Contact Information
  contact: {
    email: 'executive@bootminds.com',
    phone: '+91-80-XXXXXXX',
    address: {
      country: 'India',
      region: 'Karnataka',
      city: 'Bangalore'
    }
  },
  
  // Social Media
  social: {
    linkedin: 'https://www.linkedin.com/company/bootminds-elite'
  }
};

// Trademark / proprietary product definitions — surface on the page where each is first mentioned
export const TRADEMARK_DEFINITIONS = {
  SkillStack: {
    name: 'SkillStack™',
    short: 'Proprietary candidate assessment combining technical evaluation, behavioral fit, and cultural compatibility — driving the 25-day staffing SLA and 90-day replacement guarantee.',
    long: 'SkillStack™ is our proprietary talent assessment and verification system. Every candidate is screened for technical depth (role-specific coding, system design, or domain assessments), behavioral fit (structured interviews against role-specific competency rubrics), and cultural compatibility with the receiving team. The output is a confidence-scored shortlist that drives our 25-day staffing SLA and 90-day replacement guarantee.'
  },
  TalentCloud100: {
    name: 'Talent Cloud 100™',
    short: 'A pre-screened pipeline of 100+ vetted candidates matched to your GCC roles, mobilized in 8 weeks.',
    long: 'Talent Cloud 100™ is the pre-vetted candidate pipeline we deliver inside the GCC Setup engagement: 100+ active, role-matched candidates assessed via SkillStack™ and benchmarked against your compensation bands, ready to interview within 8 weeks of kick-off.'
  },
  EntityInABox: {
    name: 'Entity-in-a-Box™',
    short: 'Turn-key India subsidiary setup — legal entity formation, banking, statutory compliance, and director appointments — delivered Day-1-ready.',
    long: 'Entity-in-a-Box™ is our fixed-rate India subsidiary launch package: private-limited incorporation under MCA, FEMA-compliant foreign investment structuring, GST and PAN registration, banking with treasury setup, statutory registers, board resolutions, and director KYC — all completed before payroll begins.'
  },
  ManagedGCCOps: {
    name: 'Managed GCC Ops™',
    short: 'SLA-backed managed services for HR, finance, IT, admin, and facilities — letting your CXO team focus on strategy.',
    long: 'Managed GCC Ops™ wraps the back-office of your Indian GCC — HR operations, payroll, statutory compliance, finance and tax filings, IT helpdesk, facilities, and vendor management — under a single SLA. Performance metrics and dashboards roll up to your home-country leadership.'
  },
  LaunchBlueprint: {
    name: 'Launch Blueprint',
    short: 'The 90-day GCC setup methodology covering strategy, entity formation, talent, and operational launch — delivered in four parallelized phases.',
    long: 'Launch Blueprint is our four-phase 90-day GCC setup methodology: Strategic Alignment (10 days), parallel tracks for Foundation Setup (6 weeks) and Talent Acquisition (8 weeks), and Operational Launch (2 weeks). The parallel structure is what makes 90 days achievable; sequential execution would take 4-5 months.'
  }
};

// Core Value Propositions (unified messaging)
export const VALUE_PROPOSITIONS = {
  primary: {
    headline: 'Build GCCs That Out-Innovate, Not Out-Spend',
    subheadline: 'Tired of GCC delays, talent chaos, and innovation false starts? We deliver certainty: end-to-end setup, elite tech staffing, and an AI Lab to future-proof your center.',
    guarantee: '90-Day GCC Launch Guarantee'
  },
  
  competitive: {
    positioning: 'While competitors scramble, you accelerate',
    differentiator: 'The definitive authority for Fortune 500 companies establishing Global Capability Centers in India'
  },
  
  outcomes: {
    speed: '90 days to operational launch (vs 12+ months industry average); scale to full headcount over the following months',
    savings: '50–60% cost savings vs US operations (Zinnov GCC Cost Index 2024)',
    talent: 'Strict, multi-stage acceptance criteria via SkillStack™ assessment'
  }
};

// Service Offerings (unified descriptions)
export const SERVICES = {
  launchpad: {
    name: 'GCC Launchpad',
    shortDescription: 'From blueprint to profit-generating operations in 90 days',
    detailedDescription: 'Practitioner playbooks from the Bootminds team’s GCC engagements since 2018. Parallel-track execution lets you stand up an India capability center in 90 days instead of 4–5 months sequentially.',
    outcome: '50–60% cost savings vs US operations (Zinnov 2024)',
    timeline: '90 days',
    icon: 'Zap',
    features: [
      'Battle-tested legal frameworks',
      'Pre-negotiated banking partnerships',
      'Compliance automation systems',
      'Risk-free launch guarantee'
    ]
  },
  
  talent: {
    name: 'Tech Talent On Tap',
    shortDescription: 'Elite developers, data scientists, and AI specialists ready to deploy',
    detailedDescription: 'Retention benchmarked above the India IT industry average of ~80% (Aon 2024). Zero hiring lag. Multi-stage SkillStack™ assessment governs every shortlist.',
    outcome: 'Retention above India IT industry average; zero hiring lag',
    timeline: 'Immediate',
    icon: 'Users',
    features: [
      'Multi-stage SkillStack™ assessment with strict acceptance criteria',
      'Pre-vetted technical specialists',
      'Instant team scaling (up/down)',
      'Performance-guaranteed placements'
    ]
  },
  
  innovation: {
    name: 'AI Innovation Lab',
    shortDescription: 'Experiment with cutting-edge AI agents and workflows risk-free',
    detailedDescription: 'Experiment with reusable agents risk-free. Your innovation insurance policy.',
    outcome: 'Faster pilot-to-production through reusable agent libraries',
    timeline: 'Day 1 access',
    icon: 'Brain',
    features: [
      'Reusable AI agent library',
      'No-code workflow builders',
      'Innovation ROI tracking',
      'Keep every asset you build'
    ]
  }
};

// Target Audience (unified messaging)
export const TARGET_AUDIENCE = {
  primary: 'Fortune 500 enterprises',
  roles: [
    'CTOs',
    'CFOs', 
    'Global Operations Heads',
    'C-Suite Executives',
    'Business Leaders'
  ],
  
  painPoints: [
    'Slow, expensive GCC setups draining budgets and morale',
    'Scrambling to find and retain tech-qualified talent',
    'Pressure to innovate faster than competitors but stuck in execution quicksand',
    'Risk of GCC becoming a sunk cost rather than strategic asset'
  ],
  
  goals: [
    'Agile, cost-efficient GCCs that drive ROI, not headaches',
    'Beat competitors to market with innovation velocity',
    'Eliminate talent acquisition bottlenecks',
    'Transform GCC vision into profit-generating reality'
  ]
};

// Success Metrics (updated to remove exaggerated claims)
export const SUCCESS_METRICS = [
  {
    value: 'Strategic',
    label: 'GCC Planning',
    accent: 'Comprehensive blueprint development',
    icon: 'Target'
  },
  {
    value: 'Excellence',
    label: 'Talent Acquisition',
    accent: 'Elite professional recruitment',
    icon: 'Users'
  },
  {
    value: 'Quality',
    label: 'Operational Setup',
    accent: 'Best-in-class processes',
    icon: 'Award'
  },
  {
    value: 'Growth',
    label: 'Scale Support',
    accent: 'Sustainable expansion',
    icon: 'TrendingUp'
  }
];

// SEO Content (standardized)
export const SEO_CONTENT = {
  homepage: {
    title: 'Build GCCs That Drive Innovation | Bootminds',
    description: 'Transform your Global Capability Center into an innovation powerhouse with strategic planning and operational excellence. Expert guidance for successful GCC establishment.',
    keywords: 'GCC setup, Global Capability Center, India operations, business consulting, strategic planning, operational excellence, talent acquisition, business expansion'
  },
  
  services: {
    title: 'GCC Strategic Planning & Setup Services',
    description: 'End-to-end Global Capability Center planning and establishment. From blueprint to operational - comprehensive consulting services for successful GCC implementation.',
    keywords: 'GCC setup services, Global Capability Center, India operations, business setup, consulting services, strategic planning'
  },
  
  about: {
    title: 'About Bootminds - Company Foundation & Leadership',
    description: 'Meet the team behind strategic GCC consulting. Expert guidance for organizations establishing successful global capability centers.',
    keywords: 'Bootminds team, GCC consulting leadership, India business experts, strategic planning'
  }
};

// Call-to-Action Messages - Standardized to "Book a Strategy Call"
export const CTA_MESSAGES = {
  primary: 'Book a Strategy Call',
  secondary: 'View ROI Calculator',
  consultation: 'Book a Strategy Call',
  urgency: 'Book your free strategy call today',
  
  // Context-specific CTAs (all primary CTAs lead to booking)
  homepage: {
    primary: 'Book a Strategy Call',
    secondary: 'View ROI Calculator'
  },
  
  services: {
    primary: 'Book a Strategy Call',
    secondary: 'View Case Studies'
  },
  
  calculator: {
    primary: 'Book a Strategy Call',
    secondary: 'Download ROI Report'
  }
};

// Engagement Models (unified content)
export const ENGAGEMENT_MODELS = [
  {
    name: 'Company-Owned & Operated',
    description: 'You own the entity and staff; Bootminds sets it up and hands you the keys',
    bestFor: 'Full control from day one',
    riskLevel: 'Low',
    timeline: '90-120 days',
    controlLevel: 'Maximum'
  },
  {
    name: 'Joint Venture Partnership',
    description: 'We co-invest and run the centre together until you\'re ready to buy us out',
    bestFor: 'Shared risk & local insight',
    riskLevel: 'Medium',
    timeline: '60-90 days',
    controlLevel: 'Shared'
  },
  {
    name: 'Build-Operate-Transfer (BOT)',
    description: 'We build and run your GCC, then transfer full ownership when ready',
    bestFor: 'Risk-free market entry',
    riskLevel: 'Minimal',
    timeline: '45-75 days',
    controlLevel: 'Graduated'
  },
  {
    name: 'Virtual GCC (VCC)',
    description: 'Distributed teams acting as your dedicated capability center',
    bestFor: 'Fastest time to value',
    riskLevel: 'Minimal',
    timeline: '30-45 days',
    controlLevel: 'Operational'
  }
];

// FAQ Content (standardized)
export const FAQ_CONTENT = [
  {
    question: 'What makes Bootminds Elite different from other GCC consultants?',
    answer: 'We are former GCC leaders and IT services experts with 90-day launch guarantee, and proven track record with Fortune 500 companies. Our AI Innovation Lab de-risks experimentation while our battle-tested playbooks eliminate typical delays.'
  },
  {
    question: 'How can you guarantee a 90-day GCC launch?',
    answer: 'Our battle-tested playbooks, pre-negotiated partnerships, and streamlined processes eliminate typical bottlenecks. We\'ve refined our methodology through dozens of successful launches, focusing on parallel execution rather than sequential steps.'
  },
  {
    question: 'What is the AI Innovation Lab?',
    answer: 'Your dedicated sandbox for experimenting with reusable AI agents and workflows. Test new ideas without burning budgets or resources. Every asset you build is yours to keep, compressing pilot-to-production timelines through reusable agents.'
  },
  {
    question: 'How do you ensure talent quality?',
    answer: 'Our rigorous selection process includes technical assessments, cultural fit evaluation, and proven track record verification. Multi-stage SkillStack™ assessment governs every shortlist, helping deliver retention rates above the India IT industry average and zero hiring lag.'
  }
];

export default {
  COMPANY_INFO,
  VALUE_PROPOSITIONS,
  SERVICES,
  TARGET_AUDIENCE,
  SUCCESS_METRICS,
  SEO_CONTENT,
  CTA_MESSAGES,
  ENGAGEMENT_MODELS,
  FAQ_CONTENT
};